import { ColDef, IFilterParams, ValueFormatterParams } from 'ag-grid-community'
import {
  WbsItemAdditionalPropertyEntity,
  WbsItemAdditionalPropertySelectOption,
} from '../../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { Option } from '..'
import { MultiSelectCellEditor } from '../../../components/cellEditor/MultiSelectCellEditor'
import {
  ClientSideSelectFilter,
  SelectFilter,
  ServerSideSelectFilter,
} from '../../../components/filter'
import { filter } from '../../../../../pages/ProjectPlanNew/projectPlanNew'
import { wbsItemAdditionalPropertyValuesVoService } from '../../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { ColumnType } from '../../../../commons/AgGrid'

type SelectOptionMap = {
  [selectOptionUuid: string]: WbsItemAdditionalPropertySelectOption
}
export const multiSelectColumnDef = <RowData>(
  column: ColDef<RowData>,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  option: Option<RowData>
): void => {
  const selectOptionMap: SelectOptionMap = {}
  wbsItemAdditionalProperty.selectOptions?.forEach(selectOption => {
    selectOptionMap[selectOption.uuid] = selectOption
  })
  column.type = [ColumnType.multiSelectNotCustomEnum]
  column.valueFormatter = (params: ValueFormatterParams<RowData>) => {
    if (!params.value || !Array.isArray(params.value)) {
      return ''
    }
    return params.value
      .map(v => selectOptionMap[v]?.selectOption ?? '')
      .filter(v => !!v)
      .join(',')
  }
  column.cellEditor = MultiSelectCellEditor
  column.cellEditorParams = {
    options: wbsItemAdditionalProperty.selectOptions
      ? wbsItemAdditionalProperty.selectOptions.map(selectOption => ({
          label: selectOption.selectOption,
          value: selectOption.uuid,
        }))
      : [],
  }
  column.comparator = (valueA?: string[], valueB?: string[]): number => {
    const toSortedDisplayOrders = (value?: string[]): number[] =>
      (value ?? [])
        .map(uuid => selectOptionMap[uuid]?.displayOrder)
        .filter(
          (displayOrder): displayOrder is number => displayOrder !== undefined
        )
        .sort((a, b) => a - b)
    const displayOrdersA = toSortedDisplayOrders(valueA)
    const displayOrdersB = toSortedDisplayOrders(valueB)
    const length = Math.max(displayOrdersA.length, displayOrdersB.length)
    for (let i = 0; i < length; i++) {
      const a = displayOrdersA[i]
      const b = displayOrdersB[i]
      if (a === b) continue
      if (a === undefined) return -1
      if (b === undefined) return 1
      return a - b
    }
    return 0
  }
  setFilterParams(column, wbsItemAdditionalProperty, selectOptionMap, option)
  return
}
const setFilterParams = <RowData>(
  column: ColDef<RowData>,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  selectOptionMap: SelectOptionMap,
  option: Option<RowData>
) => {
  switch (option.columnFilterType) {
    case 'CLIENT_SIDE':
      column.filter = ClientSideSelectFilter
      column.floatingFilter = !option.disableFloatingFilter
      column.filterParams = {
        valueGetter: params => {
          const additionalPropertyValues = option.getAdditionalPropertyValues(
            params.node.data
          )
          if (!additionalPropertyValues) return
          return wbsItemAdditionalPropertyValuesVoService.getValue(
            additionalPropertyValues,
            wbsItemAdditionalProperty.uuid
          )
        },
        valueFormatter: params => params.value,
        getValue: o => o,
        getLabel: o => selectOptionMap[o]?.selectOption ?? '',
        sortValues: (_, options) => {
          return options.sort((a, b) => {
            const aOption = selectOptionMap[a]
            const bOption = selectOptionMap[b]
            return aOption?.displayOrder - bOption?.displayOrder
          })
        },
      }
      break
    case 'SERVER_SIDE':
      column.filter = ServerSideSelectFilter
      column.floatingFilter = !option.disableFloatingFilter
      column.filterParams = {
        fetchOptions: async (params: IFilterParams) => {
          return wbsItemAdditionalProperty.selectOptions
            ? wbsItemAdditionalProperty.selectOptions.map(selectOption => ({
                label: selectOption.selectOption,
                value: selectOption.uuid,
              }))
            : []
        },
        getValue: option => option.value,
        getLabel: option => option.label,
        fetch: (v: SelectFilter) => {
          return filter({
            projectUuid: wbsItemAdditionalProperty.projectUuid,
            additionalPropertyValues: [
              {
                wbsItemAdditionalPropertyUuid: wbsItemAdditionalProperty.uuid,
                propertyType: wbsItemAdditionalProperty.propertyType,
                values: v.values,
                includeBlank: v.includeBlank,
              },
            ],
          })
        },
        sortValues: options => {
          return options.sort((a, b) => {
            const aOption = a ? selectOptionMap[a.value] : undefined
            const bOption = b ? selectOptionMap[b.value] : undefined
            return (aOption?.displayOrder || 0) - (bOption?.displayOrder || 0)
          })
        },
      }
      break
    default:
      break
  }
}
